import type { Config } from 'unique-names-generator';
import { adjectives, animals, uniqueNamesGenerator } from 'unique-names-generator';

const MIN_LENGTH = 3;
const MAX_LENGTH = 32;
const ALLOWED = /^[a-z0-9.]+$/;

const sanitize = (value: string): string => {
    const normalized = value.trim().toLowerCase();
    if (normalized.length === 0) return '';
    const filtered = normalized
        .split('')
        .filter((char) => ALLOWED.test(char))
        .join('');
    return filtered.replace(/^[.]+/, '').replace(/[.]+$/, '');
};

const normalize = (value: string | null | undefined, enforceMinLength = true): string | null => {
    if (!value) return null;
    const sanitized = sanitize(value);
    if (!sanitized) return null;
    if (enforceMinLength && sanitized.length < MIN_LENGTH) return null;
    return sanitized;
};

const baseConfig: Config = {
    dictionaries: [adjectives, animals],
    separator: '.',
    style: 'lowerCase',
    length: 2,
};

const sanitizeCandidate = (value: string): string => {
    let nickname = sanitize(value);
    if (!nickname) return '';
    if (nickname.length < MIN_LENGTH) {
        nickname = nickname.padEnd(MIN_LENGTH, '0');
    }
    if (nickname.length > MAX_LENGTH) {
        nickname = nickname.slice(0, MAX_LENGTH);
    }
    while (nickname.endsWith('.')) {
        nickname = nickname.slice(0, -1);
    }
    return nickname;
};

const generateRaw = (): string => uniqueNamesGenerator(baseConfig);

const fallbackSuggestion = (base: string | null | undefined): string => {
    const normalized = normalize(base, false) ?? 'kalshi';
    const suffix = uniqueNamesGenerator({ ...baseConfig, length: 1 });
    return sanitizeCandidate(`${normalized}.${suffix}`);
};

export const isValidNickname = (nickname: string): boolean => {
    if (!nickname) return false;
    const sanitized = sanitizeCandidate(nickname);
    return sanitized.length >= MIN_LENGTH && sanitized.length <= MAX_LENGTH && ALLOWED.test(sanitized);
};

export const generateNicknameSuggestions = (options: { count?: number; exclude?: string } = {}): string[] => {
    const count = typeof options.count === 'number' ? options.count : 8;
    const normalizedExclude = normalize(options.exclude ?? '', false);
    const results = new Set<string>();
    const maxAttempts = count * 100;
    let attempts = 0;

    while (results.size < count && attempts < maxAttempts) {
        attempts += 1;
        const candidate = sanitizeCandidate(generateRaw());
        if (candidate.length === 0) continue;
        if (candidate === normalizedExclude) continue;
        if (!isValidNickname(candidate)) continue;
        results.add(candidate);
    }

    while (results.size < count) {
        const candidate = fallbackSuggestion(normalizedExclude);
        if (candidate.length === 0) continue;
        if (candidate === normalizedExclude) continue;
        if (!isValidNickname(candidate)) continue;
        results.add(candidate);
    }

    return Array.from(results).slice(0, count);
};

export default generateNicknameSuggestions;
